import React, { useCallback, useRef, useState } from 'react';
import { Editor } from 'vendors/@tinymce/tinymce-react/lib/es2015/main/ts/index';
import { tiny_config } from './config';
import styles from './editor.module.css';
import './editor.css';
import { Button } from 'app/shared/button';

export const TextEditor = ({
  default_value = '',
  onTextChange = () => {},
  editor_id = '',
  auto_complete_options = [],
  placeholder = 'Start typing…',
  onImageUpload = () => {},
  onFilePick = () => {},
  onContinue = null,
  actions = [],
  titleSlot = null,
  statusSlot = null,
  isContinueDisabled = false
}) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const editingBtnRef = useRef(null);

  const [btnColorOpen, setBtnColorOpen] = useState(false);
  const [btnColor, setBtnColor] = useState('#03896c');
  const [btnLabel, setBtnLabel] = useState('Click here →');
  const [btnHref, setBtnHref] = useState('#');

  const handleEditorInit = useCallback(
    (_, editor) => {
      editorRef.current = editor;

      /* Click on a styled <a> button → open customiser pre-filled */
      editor.on('click', (e) => {
        const a = e.target.closest
          ? e.target.closest('a')
          : e.target.tagName === 'A'
          ? e.target
          : null;
        if (a && (a.style.background || a.style.backgroundColor)) {
          e.preventDefault();
          editingBtnRef.current = a;
          setBtnColor(a.style.background || a.style.backgroundColor || '#03896c');
          setBtnLabel(a.textContent || '');
          setBtnHref(a.getAttribute('href') || '#');
          setBtnColorOpen(true);
        }
      });
    },
    [setBtnColor, setBtnLabel, setBtnHref, setBtnColorOpen]
  );

  const handleEditorChange = useCallback(
    (content) => {
      onTextChange(content);
    },
    [onTextChange]
  );

  const insertButtonBlock = useCallback(() => {
    setBtnColorOpen(false);
    const ed = editorRef.current;
    if (!ed) return;

    if (editingBtnRef.current) {
      const el = editingBtnRef.current;
      el.style.background = btnColor;
      el.style.backgroundColor = btnColor;
      el.setAttribute('href', btnHref);
      el.textContent = btnLabel;
      editingBtnRef.current = null;
      ed.fire('Change');
      ed.focus();
      return;
    }

    ed.insertContent(
      `<p><a href="${btnHref}" style="display:inline-block;background:${btnColor};color:#fff;padding:10px 24px;border-radius:100px;text-decoration:none;font-weight:600;">${btnLabel}</a></p>`
    );
    ed.focus();
  }, [btnColor, btnLabel, btnHref]);

  const handleImageFile = useCallback(
    (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      e.target.value = '';
      const blobInfo = {
        blob: () => file,
        filename: () => file.name,
        base64: () => ''
      };
      onImageUpload(
        blobInfo,
        (url) => {
          const ed = editorRef.current;
          if (!ed) return;
          ed.insertContent(`<p><img src="${url}" alt="" style="border-radius:8px;" /></p>`);
          ed.focus();
        },
        (err) => console.error('Image upload failed', err),
        () => {}
      );
    },
    [onImageUpload]
  );

  const config = tiny_config(onFilePick, onImageUpload, placeholder, auto_complete_options);

  return (
    <div className={styles.shell}>
      {/* ── Header bar ──────────────────────────────── */}
      <div className={styles.bar}>
        {titleSlot ? (
          <div className={styles.barTitle}>{titleSlot}</div>
        ) : (
          <div className={styles.barSpacer} />
        )}
        {statusSlot && <div className={styles.barStatus}>{statusSlot}</div>}
        <button
          className={`${styles.pill} ${styles.pillIcon}`}
          title="Undo"
          onClick={() => {
            editorRef.current?.execCommand('Undo');
            editorRef.current?.focus();
          }}
        >
          ↩
        </button>
        <button
          className={`${styles.pill} ${styles.pillIcon}`}
          title="Redo"
          onClick={() => {
            editorRef.current?.execCommand('Redo');
            editorRef.current?.focus();
          }}
        >
          ↪
        </button>
        {actions.map((action, i) => (
          <button
            key={i}
            className={`${styles.pill} ${styles.pillSecondary}`}
            onClick={action.onClick}
          >
            {action.label}
          </button>
        ))}
        {onContinue && (
          <Button
            text="Continue to send →"
            onClick={onContinue}
            className={styles.pillContinue}
            disabled={isContinueDisabled}
          />
        )}
      </div>

      {/* ── Canvas ──────────────────────────────────── */}
      <div className={styles.canvas}>
        <div className={styles.editorWrap}>
          <Editor
            id={editor_id}
            init={config}
            value={default_value}
            onEditorChange={handleEditorChange}
            onInit={handleEditorInit}
          />
        </div>
      </div>

      {/* ── Button customiser modal ──────────────────── */}
      {btnColorOpen && (
        <div className={styles.htmlOverlay}>
          <div className={styles.btnModal}>
            <div className={styles.htmlModalHeader}>
              <span className={styles.htmlModalTitle}>Customise button</span>
              <div className={styles.barSpacer} />
              <button
                className={`${styles.pill} ${styles.pillPrimary}`}
                onClick={insertButtonBlock}
              >
                Insert
              </button>
              <button
                className={`${styles.pill} ${styles.pillSecondary}`}
                onClick={() => setBtnColorOpen(false)}
              >
                Cancel
              </button>
            </div>
            <div className={styles.btnModalBody}>
              <label className={styles.btnField}>
                <span>Label</span>
                <input
                  className={styles.btnInput}
                  value={btnLabel}
                  onChange={(e) => setBtnLabel(e.target.value)}
                  placeholder="Button text"
                />
              </label>
              <label className={styles.btnField}>
                <span>Link URL</span>
                <input
                  className={styles.btnInput}
                  value={btnHref}
                  onChange={(e) => setBtnHref(e.target.value)}
                  placeholder="https://…"
                />
              </label>
              <label className={styles.btnField}>
                <span>Background colour</span>
                <div className={styles.colorRow}>
                  <input
                    type="color"
                    className={styles.colorSwatch}
                    value={btnColor}
                    onChange={(e) => setBtnColor(e.target.value)}
                  />
                  <input
                    className={styles.btnInput}
                    value={btnColor}
                    onChange={(e) => setBtnColor(e.target.value)}
                    placeholder="#03896c"
                    style={{ flex: 1 }}
                  />
                </div>
              </label>
              <div className={styles.btnPreviewRow}>
                <a
                  href="#"
                  style={{
                    display: 'inline-block',
                    background: btnColor,
                    color: '#fff',
                    padding: '10px 24px',
                    borderRadius: '100px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 14
                  }}
                  onClick={(e) => e.preventDefault()}
                >
                  {btnLabel || 'Button preview'}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Hidden image file input ──────────────────── */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImageFile}
      />
    </div>
  );
};
